import React, { createContext, useContext, useState, useEffect } from "react";
import { auth } from "../firebase";
import {
  onAuthStateChanged,
  signOut,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { getUserProfile } from "../services/userService";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Set Firebase auth persistence
    setPersistence(auth, browserLocalPersistence).catch((err) => {
      console.error("Error setting persistence:", err);
    });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          setCurrentUser(user);

          // ⚡ Load profile instantly from cache
          const cachedProfile = localStorage.getItem("userProfile");

          if (cachedProfile) {
            setUserProfile(JSON.parse(cachedProfile));
          }

          // 🔥 Fetch latest profile from Firestore
          const profile = await getUserProfile(user.uid);

          if (profile) {
            setUserProfile(profile);

            // Save profile to cache
            localStorage.setItem("userProfile", JSON.stringify(profile));
          }
        } else {
          setCurrentUser(null);
          setUserProfile(null);
          localStorage.removeItem("userProfile");
        }
      } catch (err) {
        console.error("Error fetching user profile:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);

      setCurrentUser(null);
      setUserProfile(null);
      localStorage.removeItem("userProfile");
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    error,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};